import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import useFileDownload from '@/hooks/useFiledDownload';
import CrossBorderWithdrawalContainer from './Details/CrossBorderWithdrawalContainer';
import EftFundingTransactionContainer from './Details/EftFundingTransactionContainer';
import EftWithdrawTransactionContainer from './Details/EftWithdrawTransactionContainer';
import AchFundingTransactionContainer from './Details/ACHFundingTransactionContainer';
import AchWithdrawalTransactionContainer from './Details/ACHWithdrawalTransactionContainer';
import InteracMoneyRequestTransactionContainer from './Details/InteracMoneyRequestTransactionContainer';
import InteracBulkPayoutTransactionContainer from './Details/InteracBulkPayoutTransactionContainer';
import VopayInstantWithdrawalContainer from './Details/VopayInstantWithdrawalContainer';
import RefundTransactionContainer from './Details/RefundTransactionContainer';
import CurrencyConversionTransactionContainer from './Details/CurrencyConversionTransactionContainer';
import InboundETransferContainer from './Details/InboundETransferContainer';
import VisaDirectPushTransactionContainer from './Details/VisaDirectPushTransactionContainer';
import CreditCardTransactionContainer from './Details/CreditCardTransactionContainer';
import ReversalTransactionContainer from './Details/ReversalTransactionContainer';
import RTPWithdrawalContainer from './Details/RTPWithdrawalContainer';
import FedwireFundingContainer from './Details/FedwireFundingContainer';
import FedwireWithdrawalContainer from './Details/FedwireWithdrawalContainer';
import OtherTransactionContainer from './Details/OtherTransactionContainer';
import AutoBalanceTransferTransactionContainer from './Details/AutoBalanceTransferTransactionContainer';
import DigitalWalletTransactionContainer from './Details/DigitalWalletTransactionContainer';
import AccountTransferTransactionInfo from './Details/AccountTransferTransactionInfo';
import usePostTransactionRefund from '@/hooks/useApi/usePostTransactionRefund';
import usePostTransactionCancel from '@/hooks/useApi/usePostTransactionCancel';
import usePostCancelInteracMoneyRequest from '@/hooks/useApi/usePostCancelInteracMoneyRequest';
import usePostCancelVoPayInstant from '@/hooks/useApi/usePostCancelVoPayInstant';
import usePostTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import { useDataFetchIndicator } from '@/hooks';
import usePostCancelInteracBulkPayout from '@/hooks/useApi/usePostCancelInteracBulkPayout';
import usePostVoidACHTransaction from '@/hooks/useApi/usePostVoidACHTransaction';
import usePostRefundACHTransaction from '@/hooks/useApi/usePostRefundACHTransaction';
import usePostRefundCreditCardTransaction from '@/hooks/useApi/usePostRefundCreditCardTransaction';
import useVoidCreditCardTransaction from '@/hooks/useApi/useVoidCreditCardTransaction';
import useElinx from '@/components/Elinx/ElinxHistory/Details/useElinx';
import RefundTransactionDialogue from '../Dialogue/RefundTransaction/RefundTransactionDialogue';
import RefundTransactionContext, { RefundTransactionCtx } from '../Dialogue/RefundTransaction/RefundTransactionContext';
import CancelTransactionDialogue from '../Dialogue/CancelTransaction/CancelTransactionDialogue';
import VoidTransactionDialogue from '../Dialogue/VoidTransaction/VoidTransactionDialogue';
import VoidTransactionContext, { VoidTransactionCtx } from '../Dialogue/VoidTransaction/VoidTransactionContext';
import CancelTransactionContext, { CancelTransactionCtx } from '../Dialogue/CancelTransaction/CancelTransactionContext';
import {
  RefundTransactionDialogueHandler,
  CancelTransactionDialogueHandler,
  DialogueHandlerProps,
} from '../Dialogue/types';
import styles from './Transactions.module.scss';
import { useClientData } from '@/providers/ClientData.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import ApprovalPanel from '../../UI/ApprovalPanel/ApprovalPanel';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import { Box, DropdownMenu, TransactionType, toBool } from 'vopay';
import { UtilButton, Title, Button } from 'UI';
import BillPayContainer from './Details/BillPayContainer';
import ElinxTransactionInfo from './Details/ElinxTransactionInfo';
import ResubmitTransactionButton from './ResubmitTransactionButton';

type DataProps = {
  transactionId: string;
  activeModule: string;
  hasNewTransactionButton: boolean;
  clientAccountId: string;
};
type URLParams = {
  TransactionType: string;
  TransactionID: string;
  Currency: string;
};

type FC = FunctionComponent;

const TransactionDetails: FC = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'Transaction.TransactionHistory.TransactionDetails' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const alertDialog = useAlertDialog();
  const history = useHistory();
  const { response } = useClientData();
  const { state } = useLocation<DataProps>();
  const { TransactionID } = useParams<URLParams>();
  const { onSubNavClick } = useNavMenu();
  const { printFile, downloadPdfPage } = useFileDownload();
  const [transactionId, setTransactionId] = useState('');
  const [hasNewTransaction, setHasNewTransaction] = useState(false);

  const { transactions, fetchTransactionsHistory } = usePostTransactionsHistory();
  const [, , , fetchSubAccountsAsync] = useFetchSubAccounts({});
  const [jwt, setJwt] = useState('');
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  const currentTransaction = useMemo(() => {
    return transactions.find((transaction) => transaction.TransactionID === transactionId);
  }, [transactionId, transactions]);

  const isMainAccount = !currentTransaction?.AccountID || currentTransaction.AccountID === response.AccountID;
  const { voidCreditCardTransaction } = useVoidCreditCardTransaction({ bearer: jwt });
  const { cancelElinx } = useElinx();
  const [transactionRefundResponse, postTransactionRefund] = usePostTransactionRefund({ bearer: jwt });
  const [transactionCancelResponse, postTransactionCancel] = usePostTransactionCancel({ bearer: jwt });
  const [cancelInteracBulkPayoutResponse, postCancelInteracBulkPayout] = usePostCancelInteracBulkPayout({
    bearer: jwt,
  });
  const [voidACHResponse, postVoidACH] = usePostVoidACHTransaction({ bearer: jwt });
  const [refundACHResponse, postRefundACH] = usePostRefundACHTransaction({ bearer: jwt });
  const [refundCreditCardResponse, postRefundCreditCard] = usePostRefundCreditCardTransaction({ bearer: jwt });
  const [cancelMoneyRequestResponse, postCancelMoneyRequest] = usePostCancelInteracMoneyRequest({ bearer: jwt });
  const [cancelVoPayInstantResponse, postCancelVoPayInstant] = usePostCancelVoPayInstant({ bearer: jwt });
  const { permissions } = usePermissions();
  const [shouldRefundTransaction, setShouldRefundTransaction] = useState(false);
  const [shouldCancelMoneyRequest, setShouldCancelMoneyRequest] = useState(false);

  const [shouldCancelBulkPayout, setShouldCancelBulkPayout] = useState(false);
  const [shouldCancelVoPayInstant, setShouldCancelVoPayInstant] = useState(false);
  const [shouldRefundCreditCard, setShouldRefundCreditCard] = useState(false);
  const [shouldRefundACH, setShouldRefundACH] = useState(false);
  const [shouldVoidACHt, setShouldVoidACH] = useState(false);

  const [showRefundTransactionConfirmAlert, setShowRefundTransactionConfirmAlert] = useState(false);
  const [showRefundTransactionErrorAlert, setShowRefundTransactionErrorAlert] = useState(false);
  const [sendRefundTransactionError, setSendRefundTransactionError] = useState('');
  const [shouldCancelTransaction, setShouldCancelTransaction] = useState(false);
  const [showCancelTransactionConfirmAlert, setShowCancelTransactionConfirmAlert] = useState(false);
  const [showCancelTransactionErrorAlert, setShowCancelTransactionErrorAlert] = useState(false);
  const [sendCancelTransactionError, setSendCancelTransactionError] = useState('');
  const [showVoidTransactionConfirmAlert, setShowVoidTransactionConfirmAlert] = useState(false);
  const [showVoidTransactionErrorAlert, setShowVoidTransactionErrorAlert] = useState(false);
  const [sendVoidTransactionError, setSendVoidTransactionError] = useState('');
  const RefundTransaction: RefundTransactionCtx = {
    showRefundTransactionConfirmAlert,
    showRefundTransactionErrorAlert,
    sendRefundTransactionError,
  };

  const CancelTransaction: CancelTransactionCtx = {
    showCancelTransactionConfirmAlert,
    showCancelTransactionErrorAlert,
    sendCancelTransactionError,
  };

  const VoidTransaction: VoidTransactionCtx = {
    showVoidTransactionConfirmAlert,
    showVoidTransactionErrorAlert,
    sendVoidTransactionError,
  };

  const { transactionsReset, approvalListReset } = useApprovalWorkflow();

  const transactionType = useMemo(() => {
    return currentTransaction?.TransactionType;
  }, [currentTransaction]);

  const transactionStatus = useMemo(() => {
    return currentTransaction?.TransactionStatus;
  }, [currentTransaction]);

  const transactionID = useMemo(() => {
    return currentTransaction?.TransactionID || transactionId;
  }, [currentTransaction, transactionId]);

  const handlers = {
    handleRefundTransactionDialogue: (handlerProps: DialogueHandlerProps<RefundTransactionDialogueHandler>) => {
      const { type, state: handlerState, action = 'default' } = handlerProps;

      const dialogueHandlers: RefundTransactionDialogueHandler = {
        confirm: {
          success: {
            default: () => {
              setShowRefundTransactionConfirmAlert(false);
              if (transactionType === 'Credit Card') {
                postRefundCreditCard({ TransactionID: transactionId });
                setShouldRefundCreditCard(true);
              } else if (response.Country === 'US') {
                postRefundACH({ TransactionID: transactionId });
                setShouldRefundACH(true);
              } else {
                postTransactionRefund({ TransactionID: transactionId });
                setShouldRefundTransaction(true);
              }
            },
          },
          fail: {
            default: () => {
              setShowRefundTransactionConfirmAlert(false);
            },
          },
        },
        error: {
          success: {
            default: () => {
              setShowRefundTransactionErrorAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowRefundTransactionErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][handlerState];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
    handleCancelTransactionDialogue: (handlerProps: DialogueHandlerProps<CancelTransactionDialogueHandler>) => {
      const { type, state: handlerState, action = 'default' } = handlerProps;

      const dialogueHandlers: CancelTransactionDialogueHandler = {
        alert: {
          success: {
            default: () => {
              setShowCancelTransactionConfirmAlert(false);

              if (
                transactionType === 'Interac Money Request' &&
                (transactionStatus === 'in progress' || transactionStatus === 'pending' || transactionStatus === 'sent')
              ) {
                setShouldCancelMoneyRequest(true);
                postCancelMoneyRequest({
                  TransactionID: transactionId,
                });
              } else if (
                transactionType === 'Interac Bulk Payout' &&
                (transactionStatus === 'in progress' || transactionStatus === 'pending' || transactionStatus === 'sent')
              ) {
                setShouldCancelBulkPayout(true);
                postCancelInteracBulkPayout({
                  TransactionID: transactionId,
                });
              } else if (transactionType === 'ACH Funding' || transactionType === 'ACH Withdrawal') {
                setShouldVoidACH(true);
                postVoidACH({
                  TransactionID: transactionId,
                });
              } else if (
                transactionType === 'VoPayInstant Withdraw' &&
                (transactionStatus === 'in progress' || transactionStatus === 'pending' || transactionStatus === 'sent')
              ) {
                setShouldCancelVoPayInstant(true);
                postCancelVoPayInstant({
                  TransactionID: transactionId,
                });
              } else if (transactionType === 'Credit Card' && transactionStatus === 'successful') {
                (async () => {
                  try {
                    const res = await voidCreditCardTransaction({ TransactionID: transactionId });
                    setTransactionId(res.TransactionID);
                    window.location.reload();
                    setHasNewTransaction(true);
                    history.push({
                      pathname: `/transactions-history/details/${removeWhiteSpace(
                        transactions[0].TransactionType,
                      )}/${res.TransactionID}/${transactions[0]?.Currency}`,
                      state: { transactionId: res.TransactionID },
                    });
                  } catch (err) {
                    setShowCancelTransactionErrorAlert(true);
                    setSendCancelTransactionError((err as Error).message);
                  }
                })();
              } else if (transactionType === 'eLinx') {
                (async () => {
                  try {
                    await cancelElinx({ ELinxRequestID: currentTransaction?.ELinxRequestID || '' });
                    window.location.reload();
                  } catch (err) {
                    setShowCancelTransactionErrorAlert(true);
                    setSendCancelTransactionError((err as Error).message);
                  }
                })();
              } else {
                setShouldCancelTransaction(true);
                postTransactionCancel({
                  TransactionID: transactionId,
                });
              }
            },
          },
          fail: {
            default: () => {
              setShowCancelTransactionConfirmAlert(false);
            },
          },
        },
        error: {
          success: {
            default: () => {
              setShowCancelTransactionErrorAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowCancelTransactionErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][handlerState];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
    handleVoidTransactionDialogue: (handlerProps: DialogueHandlerProps<CancelTransactionDialogueHandler>) => {
      const { type, state: handlerState, action = 'default' } = handlerProps;

      const dialogueHandlers: CancelTransactionDialogueHandler = {
        alert: {
          success: {
            default: () => {
              setShowVoidTransactionConfirmAlert(false);

              postVoidACH({
                TransactionID: transactionId,
              });
              setShouldVoidACH(true);
            },
          },
          fail: {
            default: () => {
              setShowVoidTransactionConfirmAlert(false);
            },
          },
        },
        error: {
          success: {
            default: () => {
              setShowVoidTransactionErrorAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowVoidTransactionErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][handlerState];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  useEffect(() => {
    setTransactionId(TransactionID);
    setHasNewTransaction(true);
    transactionsReset();
    approvalListReset();
  }, [TransactionID]);

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!transactionId || !hasNewTransaction) return;

    const load = async () => {
      try {
        setDataFetchingInfo(true);
        const res = await fetchTransactionsHistory({
          StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
          EndDateTime: moment().utc().format('YYYY-MM-DD'),
          TransactionID,
          IncludeSubaccountTransactions: true,
        });
        if (!res.Success) throw new Error(res.ErrorMessage);
        setInitialized(true);
      } catch {
        alertDialog.error(tGlobal('error'), tGlobal('errorOccurred'));
      } finally {
        setDataFetchingInfo(false);
      }
    };

    setHasNewTransaction(false);
    load();
  }, [transactionId, hasNewTransaction]);

  useEffect(() => {
    if (shouldRefundTransaction) {
      const { Success, ErrorMessage } = transactionRefundResponse;

      if (Success === true) {
        setTransactionId(transactionRefundResponse.TransactionID);

        history.push({
          pathname: `/transactions-history/details/Refund/${transactionRefundResponse.TransactionID}/${transactions[0]?.Currency}`,
          state: { transactionId: transactionRefundResponse.TransactionID },
        });
      } else {
        setShowRefundTransactionErrorAlert(true);
        setSendRefundTransactionError(ErrorMessage);
      }
      setShouldRefundTransaction(false);
    }
    if (shouldRefundACH && refundACHResponse) {
      const { Success, ErrorMessage } = refundACHResponse;

      if (Success === true) {
        setTransactionId(refundACHResponse.TransactionID);
        history.push({
          pathname: `/transactions-history/details/Refund/${refundACHResponse.TransactionID}/${transactions[0]?.Currency}`,
          state: { transactionId: refundACHResponse.TransactionID },
        });
      } else {
        setShowRefundTransactionErrorAlert(true);
        setSendRefundTransactionError(ErrorMessage);
      }
      setShouldRefundACH(false);
    }
    if (shouldRefundCreditCard && refundCreditCardResponse) {
      const { Success, ErrorMessage } = refundCreditCardResponse;

      if (Success === true) {
        setTransactionId(refundCreditCardResponse.TransactionID);
        history.push({
          pathname: `/transactions-history/details/Refund/${refundCreditCardResponse.TransactionID}/${transactions[0]?.Currency}`,
          state: { transactionId: refundCreditCardResponse.TransactionID },
        });
      } else {
        setShowRefundTransactionErrorAlert(true);
        setSendRefundTransactionError(ErrorMessage);
      }
      setShouldRefundCreditCard(false);
    }
  }, [transactionRefundResponse, refundACHResponse, refundCreditCardResponse]);

  useEffect(() => {
    if (shouldCancelTransaction) {
      const { Success, ErrorMessage } = transactionCancelResponse;

      if (Success === true) {
        setTransactionId(transactionCancelResponse.TransactionID);
        window.location.reload();
        setHasNewTransaction(true);
        history.push({
          pathname: `/transactions-history/details/${removeWhiteSpace(
            transactions[0].TransactionType,
          )}/${transactionCancelResponse.TransactionID}/${transactions[0]?.Currency}`,
          state: { transactionId: transactionCancelResponse.TransactionID },
        });
      } else {
        setShowCancelTransactionErrorAlert(true);
        setSendCancelTransactionError(ErrorMessage);
      }
      setShouldCancelTransaction(false);
    }
    if (shouldCancelMoneyRequest) {
      const { Success, ErrorMessage } = cancelMoneyRequestResponse;

      if (Success === true) {
        setTransactionId(cancelMoneyRequestResponse.TransactionID);
        window.location.reload();
        setHasNewTransaction(true);
        history.push({
          pathname: `/transactions-history/details/InteracMoneyRequest/${cancelMoneyRequestResponse.TransactionID}/${transactions[0]?.Currency}`,
          state: { transactionId: cancelMoneyRequestResponse.TransactionID },
        });
      } else {
        setShowCancelTransactionErrorAlert(true);
        setSendCancelTransactionError(ErrorMessage);
      }
      setShouldCancelMoneyRequest(false);
    }
    if (shouldVoidACHt && voidACHResponse) {
      const { Success, ErrorMessage } = voidACHResponse;

      if (Success === true) {
        setTransactionId(voidACHResponse.TransactionID);
        window.location.reload();
        setHasNewTransaction(true);
        history.push({
          pathname: `/transactions-history/details/${removeWhiteSpace(
            transactions[0].TransactionType,
          )}/${voidACHResponse.TransactionID}/${transactions[0]?.Currency}`,
          state: { transactionId: voidACHResponse.TransactionID },
        });
      } else {
        setShowVoidTransactionErrorAlert(true);
        setSendVoidTransactionError(ErrorMessage);
      }
      setShouldVoidACH(false);
    }
    if (shouldCancelBulkPayout) {
      const { Success, ErrorMessage } = cancelInteracBulkPayoutResponse;

      if (Success === true) {
        setTransactionId(cancelInteracBulkPayoutResponse.TransactionID);
        window.location.reload();
        setHasNewTransaction(true);
        history.push({
          pathname: `/transactions-history/details/${removeWhiteSpace(
            transactions[0].TransactionType,
          )}/${cancelInteracBulkPayoutResponse.TransactionID}/${transactions[0]?.Currency}`,
          state: { transactionId: cancelInteracBulkPayoutResponse.TransactionID },
        });
      } else {
        setShowCancelTransactionErrorAlert(true);
        setSendCancelTransactionError(ErrorMessage);
      }
      setShouldCancelBulkPayout(false);
    }

    if (shouldCancelVoPayInstant) {
      const { Success, ErrorMessage } = cancelVoPayInstantResponse;

      if (Success === true) {
        setTransactionId(cancelVoPayInstantResponse.TransactionID);
        window.location.reload();
        setHasNewTransaction(true);
        history.push({
          pathname: `/transactions-history/details/${removeWhiteSpace(
            transactions[0].TransactionType,
          )}/${cancelVoPayInstantResponse.TransactionID}/${transactions[0]?.Currency}`,
          state: { transactionId: cancelVoPayInstantResponse.TransactionID },
        });
      } else {
        setShowCancelTransactionErrorAlert(true);
        setSendCancelTransactionError(ErrorMessage);
      }
      setShouldCancelVoPayInstant(false);
    }
  }, [
    transactionCancelResponse,
    cancelMoneyRequestResponse,
    voidACHResponse,
    cancelInteracBulkPayoutResponse,
    cancelVoPayInstantResponse,
  ]);

  useEffect(() => {
    if (!currentTransaction?.AccountID) return;
    if (currentTransaction.AccountID === response.AccountID) {
      setJwt(response.JWT || '');
      return;
    }

    const loadToken = async () => {
      try {
        setDataFetchingInfo(true);
        const res = await fetchSubAccountsAsync({
          SubaccountID: currentTransaction.AccountID,
          ShowToken: true,
        });
        if (!res.Success) throw new Error(res.ErrorMessage);
        const match = Object.values(res.Subaccounts || {}).find((s) => s.AccountID === currentTransaction.AccountID);
        setJwt(match?.Token ?? '');
      } catch {
        alertDialog.error(tGlobal('error'), tGlobal('errorOccurred'));
      } finally {
        setDataFetchingInfo(false);
      }
    };

    loadToken();
  }, [currentTransaction?.AccountID]);

  const title =
    transactionType === 'Reversal'
      ? t('returnOverview', { defaultValue: 'Return Transaction Overview' })
      : transactionType === 'Account Transfer'
        ? t('transferOverview', { defaultValue: 'Transfer Overview' })
        : t('transactionOverview', { defaultValue: 'Transaction Overview' });

  return (
    <RefundTransactionContext.Provider value={RefundTransaction}>
      <CancelTransactionContext.Provider value={CancelTransaction}>
        <VoidTransactionContext.Provider value={VoidTransaction}>
          <div className={styles.wrapper}>
            <Box flx="col" gapSize="16px">
              <UtilButton
                iconLeft="chevron_left"
                size="large"
                onClick={() => {
                  if (history.length > 1) {
                    history.goBack();
                  } else {
                    history.replace('/transactions-history');
                  }
                }}
              >
                {tGlobal('Buttons.back', { defaultValue: 'Back' })}
              </UtilButton>
              <Box width="100%" flx="row" jc="sb" maxheight="43px">
                <Box jc="fs" ai="center" gap="common">
                  <Title>{title}</Title>
                  {state?.hasNewTransactionButton && (
                    <Button
                      id="start-new-transactions"
                      colorScheme="primary"
                      onClick={() => {
                        if (state.activeModule === 'fundMyAccount') {
                          onSubNavClick(
                            'fundMyAccount',
                            '/fund-my-account',
                            t('fundMyAccount', { defaultValue: 'Fund My Account' }),
                          );
                        } else if (state.activeModule === 'bankAccountTransfer') {
                          onSubNavClick(
                            'bankAccountTransfer',
                            '/bank-account-transfer',
                            t('bankAccountTransfer', { defaultValue: 'Bank Account Transfer' }),
                          );
                        } else if (state.activeModule === 'withdrawMyAccount') {
                          onSubNavClick(
                            'withdrawMyAccount',
                            '/withdraw-my-account',
                            t('withdrawMyAccount', { defaultValue: 'Withdraw My Account' }),
                          );
                        } else if (state.activeModule === 'singleCollection') {
                          onSubNavClick(
                            'singleCollection',
                            '/single-collection',
                            t('collection', { defaultValue: 'Collection' }),
                          );
                        } else if (state.activeModule === 'singlePayment') {
                          onSubNavClick('singlePayment', '/single-payment', t('payment', { defaultValue: 'Payment' }));
                        } else if (state.activeModule === 'accountTransfer') {
                          onSubNavClick(
                            'accountTransfer',
                            '/sub-account-transfer',
                            t('accountTransfer', { defaultValue: 'Account Transfer' }),
                          );
                        }
                      }}
                    >
                      {t('startNew', { defaultValue: 'Start New Transaction' })}
                    </Button>
                  )}
                </Box>
                <Box flx="row" gap="sm">
                  {((transactionType === 'EFT Funding' &&
                    transactionStatus === 'successful' &&
                    currentTransaction?.IsRefunded === false) ||
                    (transactionType === 'EFT Withdrawal' &&
                      transactionStatus === 'successful' &&
                      currentTransaction?.IsRefunded === false) ||
                    (transactionType === 'Credit Card' &&
                      (transactionStatus === 'settled' || transactionStatus === 'successful') &&
                      currentTransaction?.IsRefunded === false) ||
                    (transactionType === 'ACH Funding' &&
                      (transactionStatus === 'settled' || transactionStatus === 'successful') &&
                      currentTransaction?.IsRefunded === false) ||
                    (transactionType === 'ACH Withdrawal' &&
                      (transactionStatus === 'settled' || transactionStatus === 'successful') &&
                      currentTransaction?.IsRefunded === false)) &&
                  isMainAccount &&
                  permissions.TransactionCancellation ? (
                    <Button
                      id="refund-transaction"
                      color="secondary"
                      onClick={() => {
                        setShowRefundTransactionConfirmAlert(true);
                      }}
                    >
                      {t('refund', { defaultValue: 'Refund' })}
                    </Button>
                  ) : (
                    <></>
                  )}

                  {transactionStatus === 'pending approval' &&
                    (transactionType === 'EFT Funding' ||
                      transactionType === 'EFT Withdrawal' ||
                      transactionType === 'VoPayInstant Withdraw' ||
                      transactionType === 'Interac Money Request' ||
                      transactionType === 'eLinx') &&
                    permissions.TransactionCancellation && (
                      <Button
                        id="cancel-transaction"
                        color="secondary"
                        onClick={() => {
                          setShowCancelTransactionConfirmAlert(true);
                        }}
                      >
                        {tGlobal('cancel', { defaultValue: 'Cancel' })}
                      </Button>
                    )}

                  {transactionType === 'Credit Card' &&
                  transactionStatus === 'successful' &&
                  permissions.TransactionCancellation ? (
                    <Button
                      id="void-credit-card-transaction"
                      color="secondary"
                      onClick={() => {
                        setShowCancelTransactionConfirmAlert(true);
                      }}
                    >
                      {tGlobal('cancel', { defaultValue: 'Cancel' })}
                    </Button>
                  ) : (
                    <></>
                  )}
                  {((transactionType === 'EFT Funding' &&
                    (transactionStatus === 'pending' ||
                      (transactionStatus === 'waiting' && toBool(response.ClientAccountsEnabled)))) ||
                    (transactionType === 'EFT Withdrawal' &&
                      (transactionStatus === 'pending' ||
                        (transactionStatus === 'waiting' && toBool(response.ClientAccountsEnabled)))) ||
                    (transactionType === 'Interac Money Request' &&
                      (transactionStatus === 'requested' ||
                        (transactionStatus === 'waiting' && toBool(response.ClientAccountsEnabled)))) ||
                    (transactionType === 'Interac Bulk Payout' &&
                      (transactionStatus === 'requested' ||
                        (transactionStatus === 'waiting' && toBool(response.ClientAccountsEnabled)))) ||
                    (transactionType === 'Account Transfer' && transactionStatus === 'waiting')) &&
                  isMainAccount &&
                  permissions.TransactionCancellation ? (
                    <Button
                      id="cancel-transaction"
                      color="secondary"
                      onClick={() => {
                        setShowCancelTransactionConfirmAlert(true);
                      }}
                    >
                      {t('cancel', { defaultValue: 'Cancel' })}
                    </Button>
                  ) : (
                    <></>
                  )}
                  {((transactionType === 'ACH Funding' && transactionStatus === 'pending') ||
                    (transactionType === 'ACH Withdrawal' && transactionStatus === 'pending')) &&
                  isMainAccount &&
                  permissions.TransactionCancellation ? (
                    <Button
                      id="cancel-transaction"
                      color="secondary"
                      onClick={() => {
                        setShowCancelTransactionConfirmAlert(true);
                      }}
                    >
                      {t('void', { defaultValue: 'Void' })}
                    </Button>
                  ) : (
                    <></>
                  )}
                  {((transactionType === 'Interac Money Request' &&
                    (transactionStatus === 'in progress' ||
                      transactionStatus === 'pending' ||
                      transactionStatus === 'sent' ||
                      transactionStatus === 'waiting')) ||
                    (transactionType === 'Interac Bulk Payout' &&
                      (transactionStatus === 'in progress' ||
                        transactionStatus === 'pending' ||
                        transactionStatus === 'sent' ||
                        transactionStatus === 'waiting')) ||
                    (transactionType === 'VoPayInstant Withdraw' &&
                      (transactionStatus === 'in progress' ||
                        transactionStatus === 'pending' ||
                        transactionStatus === 'sent' ||
                        transactionStatus === 'waiting'))) &&
                  isMainAccount &&
                  permissions.TransactionCancellation ? (
                    <Button
                      id="cancel-transaction"
                      color="secondary"
                      onClick={() => {
                        setShowCancelTransactionConfirmAlert(true);
                      }}
                    >
                      {t('Buttons.cancel', { defaultValue: 'Cancel' })}
                    </Button>
                  ) : (
                    <></>
                  )}

                  {toBool(response.TransactionResubmissionEnabled) && transactionType && currentTransaction && jwt && (
                    <ResubmitTransactionButton
                      key={transactionID}
                      transactionId={transactionID}
                      transactionType={transactionType}
                      currency={currentTransaction.Currency}
                      bearer={jwt}
                    />
                  )}

                  <DropdownMenu>
                    <DropdownMenu.Trigger asChild>
                      <Button iconRight="keyboard_arrow_down">
                        {tGlobal('ExportDropdown.label', { defaultValue: 'Export' })}
                      </Button>
                    </DropdownMenu.Trigger>

                    <DropdownMenu.Content align="end">
                      <DropdownMenu.Item
                        onSelect={() => {
                          downloadPdfPage('transaction-overview', title);
                        }}
                      >
                        {tGlobal('ExportDropdown.Options.pdf', { defaultValue: 'PDF' })}
                      </DropdownMenu.Item>
                      <DropdownMenu.Item
                        onSelect={() => {
                          printFile('transaction-overview');
                        }}
                      >
                        {tGlobal('ExportDropdown.Options.print', { defaultValue: 'Print' })}
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu>
                </Box>
              </Box>
              {useMemo(
                () => (
                  <>
                    {initialized && jwt && (
                      <div id="transaction-overview" className={styles.overviewWrapper}>
                        <Box flx="col" width="50%">
                          {transactionStatus === 'pending approval' && (
                            <ApprovalPanel
                              className={styles.approverPanel}
                              title={t('Approval.statusPending', { defaultValue: 'Pending Approval' })}
                              description={t('Approval.description', {
                                defaultValue: 'Approvers can find these transaction(s) in their Approval Inbox',
                              })}
                            />
                          )}

                          {currentTransaction?.Notes === 'Scheduled Auto Balance Transfer' ? (
                            <AutoBalanceTransferTransactionContainer
                              transactionID={transactionID}
                              transactionHeader={currentTransaction}
                              jwt={jwt}
                            />
                          ) : transactionType === TransactionType.CROSSBORDER_WITHDRAWAL ? (
                            <CrossBorderWithdrawalContainer
                              transactionId={transactionID}
                              notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                            />
                          ) : transactionType === TransactionType.EFT_FUNDING ? (
                            <EftFundingTransactionContainer
                              TransactionID={transactionID}
                              Notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                            />
                          ) : transactionType === TransactionType.EFT_WITHDRAWAL ? (
                            <EftWithdrawTransactionContainer
                              TransactionID={transactionID}
                              Notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                            />
                          ) : transactionType === TransactionType.ACH_FUNDING ? (
                            <AchFundingTransactionContainer
                              TransactionID={transactionID}
                              Notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                            />
                          ) : transactionType === TransactionType.ACH_WITHDRAWAL ? (
                            <AchWithdrawalTransactionContainer
                              TransactionID={transactionID}
                              Notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                            />
                          ) : transactionType === TransactionType.INTERACT_MONEY_REQUEST ? (
                            <InteracMoneyRequestTransactionContainer
                              TransactionID={transactionID}
                              Notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                            />
                          ) : transactionType === TransactionType.INTERACT_BULK_PAYOUT ? (
                            <InteracBulkPayoutTransactionContainer
                              TransactionID={transactionID}
                              Notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                            />
                          ) : transactionType === TransactionType.VOPAYINSTANT_WITHDRAWAL ? (
                            <VopayInstantWithdrawalContainer
                              TransactionID={transactionID}
                              Notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                            />
                          ) : transactionType === TransactionType.REFUND ? (
                            <RefundTransactionContainer TransactionID={transactionID} jwt={jwt} />
                          ) : transactionType === TransactionType.CURRENCY_CONVERSION ? (
                            <CurrencyConversionTransactionContainer TransactionID={transactionID} jwt={jwt} />
                          ) : transactionType === TransactionType.INBOUND_E_TRANSFER ? (
                            <InboundETransferContainer TransactionID={transactionID} jwt={jwt} />
                          ) : transactionType === TransactionType.VISA_DIRECT_PUSH ? (
                            <VisaDirectPushTransactionContainer
                              TransactionID={transactionID}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                            />
                          ) : transactionType === TransactionType.RTP_WITHDRAWAL ? (
                            <RTPWithdrawalContainer
                              TransactionID={transactionID}
                              Notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                            />
                          ) : transactionType === TransactionType.FEDWIRE_FUNDING ? (
                            <FedwireFundingContainer
                              TransactionID={transactionID}
                              Notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                            />
                          ) : transactionType === TransactionType.FEDWIRE_WITHDRAWAL ? (
                            <FedwireWithdrawalContainer
                              TransactionID={transactionID}
                              Notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                            />
                          ) : transactionType === TransactionType.CREDIT_CARD ? (
                            <CreditCardTransactionContainer
                              TransactionID={transactionID}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                            />
                          ) : transactionType === TransactionType.REVERSAL ? (
                            <ReversalTransactionContainer TransactionID={transactionID} jwt={jwt} />
                          ) : transactionType === TransactionType.GOOGLE_PAY ? (
                            <DigitalWalletTransactionContainer
                              TransactionID={transactionID}
                              transactionType="GooglePay"
                              fullName={currentTransaction?.FullName || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                            />
                          ) : transactionType === TransactionType.VENMO_WITHDRAWAL ? (
                            <DigitalWalletTransactionContainer
                              TransactionID={transactionID}
                              transactionType="Venmo"
                              fullName={currentTransaction?.FullName || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                            />
                          ) : transactionType === TransactionType.PAYPAL_WITHDRAWAL ? (
                            <DigitalWalletTransactionContainer
                              TransactionID={transactionID}
                              transactionType="PayPal"
                              fullName={currentTransaction?.FullName || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                            />
                          ) : transactionType === TransactionType.BILL_PAY ? (
                            <BillPayContainer TransactionID={transactionId} jwt={jwt} />
                          ) : transactionType === TransactionType.ACCOUNT_TRANSFER && currentTransaction ? (
                            <AccountTransferTransactionInfo
                              transaction={currentTransaction}
                              transactionID={transactionID}
                              jwt={jwt}
                            />
                          ) : transactionType === TransactionType.ELINX && currentTransaction?.ELinxRequestID ? (
                            <ElinxTransactionInfo ELinxRequestID={currentTransaction.ELinxRequestID} jwt={jwt} />
                          ) : (
                            <OtherTransactionContainer />
                          )}
                        </Box>
                      </div>
                    )}
                  </>
                ),
                [transactionId, transactions, jwt],
              )}
            </Box>
          </div>
          <RefundTransactionDialogue handleDialogue={handlers.handleRefundTransactionDialogue} />
          <CancelTransactionDialogue handleDialogue={handlers.handleCancelTransactionDialogue} />
          <VoidTransactionDialogue handleDialogue={handlers.handleVoidTransactionDialogue} />
        </VoidTransactionContext.Provider>
      </CancelTransactionContext.Provider>
    </RefundTransactionContext.Provider>
  );

  function removeWhiteSpace(TransactionType: string) {
    return decodeURI(TransactionType).replaceAll(' ', '');
  }
};

export default TransactionDetails;
